/* eslint react-hooks/exhaustive-deps: "warn" */

import { useEffect, useRef, useCallback, useMemo, useContext, useState, type ReactNode } from 'react';

import cn from 'classnames';

import { UserCountryContext } from 'context/UserCountryContext';
import { ContentContext } from 'context/ContentContext';

import { useIntersection } from 'hooks/useIntersection';

import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';

import { PricingBlockCards } from 'features/PAYG/organisms/PricingBlock/PricingBlockCards';
import { FrequencySwitch } from 'features/product-pricing/organisms/FrequencySwitch/FrequencySwitch';

import styles from './PricingBlock.module.scss';

import { prices, data } from './data';

type Props = {
  id?: string;
  title?: string;
  description?: string;
  extra?: ReactNode;
  theme?: 'light' | 'dark';
  className?: string;
  currency?: keyof typeof prices;
  hasFeatureKeys?: boolean;
  productTypes?: string[];
};

const baseClass = 'gdb-pricing-block';

const frequencies = [
  { value: 'month', label: 'Bill monthly' },
  { value: 'year', label: 'Bill yearly', discount: 15 },
];

export const PricingBlock = ({
  id,
  title,
  description,
  extra,
  theme = 'light',
  className,
  hasFeatureKeys,
  productTypes = ['starter', 'professional', 'custom'],
}: Props) => {
  const { config } = useContext(ContentContext);
  const { countryCode, loading: countryLoading, fetchCountryCode } = useContext(UserCountryContext);

  const ref = useRef<HTMLDivElement>(null);
  const intersected = useIntersection(ref);

  const [frequency, setFrequency] = useState<'month' | 'year'>(config.is_yearly_default_on_pricing ? 'year' : 'month');

  const currency = useMemo(() => {
    if (!countryCode) {
      return 'GBP';
    }

    return ['gb', 'uk'].includes(countryCode) ? 'GBP' : 'EUR';
  }, [countryCode]);

  useEffect(() => {
    if (intersected) {
      fetchCountryCode();
    }
  }, [intersected, fetchCountryCode]);

  const products = useMemo(() => {
    return data
      .filter((item) => productTypes.includes(item.type))
      .map((item) => {
        const price = prices[currency][item.type];

        return {
          ...item,
          price_explanation:
            frequency === 'year' && item.price_explanation_year ? item.price_explanation_year : item.price_explanation,
          ...(price && {
            price: price[frequency],
            link: price[`${frequency}_link`],
          }),
        };
      });
  }, [currency, frequency, productTypes]);

  const onChangeFrequency = useCallback((value: string) => setFrequency(value as typeof frequency), []);

  return (
    <section
      ref={ref}
      id={id}
      className={cn(styles[`${baseClass}-wrapper`], styles[`${baseClass}-${theme}`], className)}
    >
      <div className={styles[`${baseClass}-container`]}>
        {(title || description) && <TitleBlock title={title} description={description} extra={extra} theme={theme} />}

        <FrequencySwitch
          discountLabel="Save up to {amount}%"
          data={frequencies}
          value={frequency}
          onChange={onChangeFrequency}
        />

        <PricingBlockCards
          loading={countryLoading}
          theme={theme}
          data={products}
          currency={currency}
          hasFeatureKeys={hasFeatureKeys}
        />
      </div>
    </section>
  );
};
